import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";
import SummaryAccountDetailContent from "./SummaryAccountDetailContent";
import SummaryKpiCards from "../Dashboard/SummaryCard/SummaryKpiCards";
import SummarySeverityChart from "../Dashboard/SummaryCard/SummarySeverityChart";
import SummaryComplianceChart from "../Dashboard/SummaryCard/SummaryComplianceChart";
import SummaryCostChart from "../Dashboard/SummaryCard/SummaryCostChart";
import SummaryFrameworkComplianceChart from "../Dashboard/SummaryCard/SummaryFrameworkComplianceChart";
import { useAccountSummaryData } from "./useAccountSummaryData";

const ACCOUNTS_PAGE_SIZE = 100;

const getAccountLabel = (account) => {
  const num = account?.account_number ?? account?.accountNumber ?? account?.id ?? "";
  const name = account?.name ?? account?.account_name ?? "";
  if (name && name !== num) return `${name} (${num})`;
  return num || "—";
};

const SummaryPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState("");

  // Summary for selected account (or org-wide when not selected)
  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
  } = useAccountSummaryData(selectedAccountNumber);
  const overall = summaryData?.overall ?? null;
  const services = summaryData?.services ?? [];

  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(null);
    try {
      const response = await get(url.GET_ARN_VIEWS_ACCOUNTS, {
        params: { page: 1, page_size: ACCOUNTS_PAGE_SIZE },
      });
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const items = response?.items ?? [];
        const list = Array.isArray(items) ? items : [];
        setAccounts(list);
        if (list.length > 0) {
          const first = list[0];
          const num = first?.account_number ?? first?.accountNumber ?? first?.id ?? "";
          setSelectedAccountNumber((prev) => (prev ? prev : num));
        }
      } else {
        setAccountsError(getApiErrorMessage(response) || "Failed to load accounts");
      }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      setAccountsError(getApiErrorMessage(errData) || err?.message || "Failed to load accounts");
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: t.colors.background,
        minHeight: "100vh",
        fontFamily: t.fontFamily,
      }}
    >
      {/* Page title + account selector (applies to summary + details) */}
      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: t.colors.text,
              fontSize: "1.25rem",
              letterSpacing: "-0.01em",
              mb: 0.25,
              lineHeight: 1.2,
              fontFamily: t.fontFamily,
            }}
          >
            ARNs Insight
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: t.colors.textMuted,
              fontSize: "0.8125rem",
              fontWeight: 400,
              lineHeight: 1.5,
              fontFamily: t.fontFamily,
            }}
          >
            Organization summary and account-level CSPM &amp; FinOps view.
          </Typography>
        </Box>
        <Box sx={{ minWidth: { xs: "100%", md: 280 }, maxWidth: 360 }}>
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: t.fontFamily,
                fontSize: "0.875rem",
                backgroundColor: t.colors.card,
                borderRadius: t.radius.md,
                "& fieldset": { borderColor: t.colors.border },
                "&:hover fieldset": { borderColor: t.colors.textMuted },
                "&.Mui-focused fieldset": { borderColor: t.colors.accent, borderWidth: "1px" },
              },
            }}
          >
            <InputLabel id="summary-account-label" sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>
              View account
            </InputLabel>
            <Select
              labelId="summary-account-label"
              id="summary-account-select"
              value={selectedAccountNumber || ""}
              label="View account"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedAccountNumber(value);
                // Reset filters when switching accounts to avoid stale filters
                setSeverityFilter("");
                setFrameworkFilter("");
              }}
              sx={{ fontFamily: t.fontFamily, fontWeight: 500, "& .MuiSelect-select": { py: 1.25 } }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1.5,
                    borderRadius: t.radius.md,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxHeight: 320,
                    "& .MuiMenuItem-root": { fontFamily: t.fontFamily, fontSize: "0.875rem" },
                  },
                },
              }}
            >
              {accountsLoading ? (
                <MenuItem value="" disabled>Loading…</MenuItem>
              ) : accountsError ? (
                <MenuItem value="" disabled>{accountsError}</MenuItem>
              ) : (
                accounts.map((acc, i) => {
                  const num = acc?.account_number ?? acc?.accountNumber ?? acc?.id ?? "";
                  if (!num) return null;
                  return (
                    <MenuItem key={num || i} value={num}>
                      {getAccountLabel(acc)}
                    </MenuItem>
                  );
                })
              )}
              {!accountsLoading && !accountsError && accounts.length === 0 && (
                <MenuItem value="" disabled>No accounts</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Top half: KPI cards and charts */}
      <Box
        sx={{
          borderRadius: t.radius.lg,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          boxShadow: t.shadow,
          p: 2,
          mb: 3,
        }}
      >
        {summaryLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4 }}>
            <CircularProgress size={24} sx={{ color: t.colors.accent }} />
            <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading summary…</Typography>
          </Box>
        )}
        {summaryError && (
          <Typography sx={{ color: t.colors.destructive, fontSize: "0.875rem", fontFamily: t.fontFamily }}>{summaryError}</Typography>
        )}
        {!summaryLoading && !summaryError && (
          <>
            <Box sx={{ mb: 2 }}>
              <SummaryKpiCards overall={overall} />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                gap: 2,
              }}
            >
              <SummarySeverityChart
                securityFailedBySeverity={overall?.security_failed_checks_by_severity_total}
                onSeverityClick={(sev) =>
                  setSeverityFilter((prev) => (prev === sev ? "" : sev))
                }
              />
              <SummaryComplianceChart overall={overall} services={services} />
              <SummaryFrameworkComplianceChart
                overall={overall}
                framework={frameworkFilter}
                onFrameworkChange={(fw) =>
                  setFrameworkFilter((prev) => (prev === fw ? "" : fw))
                }
              />
              <SummaryCostChart overall={overall} services={services} />
            </Box>
          </>
        )}
      </Box>

      {/* Bottom half: Account details */}
      <Box
        sx={{
          borderRadius: t.radius.lg,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          boxShadow: t.shadow,
          p: 2,
        }}
      >
        <SummaryAccountDetailContent
          accountNumber={selectedAccountNumber}
          showBackLink={false}
          severity={severityFilter}
          onSeverityChange={setSeverityFilter}
          framework={frameworkFilter}
          onFrameworkChange={setFrameworkFilter}
          frameworks={Array.isArray(overall?.compliance_by_framework) ? overall.compliance_by_framework : []}
        />
      </Box>
    </Box>
  );
};

export default SummaryPage;
